class CasesTraysForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.priceElement = document.querySelector(`#price-${this.dataset.sectionId}`);
    this.thicknessSelect = this.querySelector('[data-thickness-select]');
    this.colorSelect = this.querySelector('[data-color-select]');
    this.addToCartButton = this.querySelector('[data-add-to-cart-button]');
    this.shippingInfo = this.querySelector('[data-shipping-info]');

    this.caseLengthInput = this.querySelector('[data-case-length]');
    this.caseWidthInput = this.querySelector('[data-case-width]');
    this.caseHeightInput = this.querySelector('[data-case-height]');
    this.casesVariantSelect = this.querySelector('[data-cases-variant-select]');

    this.dimensionsCheckboxes = this.querySelectorAll('[data-dimensions-option]');

    this.currentVariant = null;
    this.currentPricing = {};

    this.debouncedUpdate = this.debounce(this.updateCalculation.bind(this), 300);

    // Properties for AJAX cart
    this.cartNotification = document.querySelector('cart-notification');
    this.body = document.body;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

    this.bindEvents();
    this.updateColorOptions();
    this.updateAddToCartState();
  }

  parseFraction(value) {
    if (!value) return 0;
    const str = value.toString().trim().replace('"', '');
    if (str.includes('/')) {
      const parts = str.split('/');
      return parseFloat(parts[0]) / parseFloat(parts[1]);
    }
    return parseFloat(str) || 0;
  }

  debounce(fn, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), delay);
    };
  }

  bindEvents() {
    this.thicknessSelect.addEventListener('change', this.onThicknessChange.bind(this));
    if (this.colorSelect) {
      this.colorSelect.addEventListener('change', this.onColorChange.bind(this));
    }
    this.caseLengthInput.addEventListener('input', this.debouncedUpdate);
    this.caseWidthInput.addEventListener('input', this.debouncedUpdate);
    this.caseHeightInput.addEventListener('input', this.debouncedUpdate);

    this.dimensionsCheckboxes.forEach(checkbox => checkbox.addEventListener('change', this.handleDimensionsChange.bind(this)));
  }

  onThicknessChange() {
    this.updateColorOptions();
    this.updateVariantSelection();
  }

  onColorChange() {
    this.updateVariantSelection();
  }

  handleDimensionsChange(event) {
    const clickedCheckbox = event.currentTarget;
    const value = clickedCheckbox.dataset.dimensionsOption;

    const hiddenInput = this.querySelector('[data-dimensions-value]');
    const otherCheckboxes = Array.from(this.dimensionsCheckboxes).filter(cb => cb !== clickedCheckbox);

    if (clickedCheckbox.checked) {
      // Uncheck other dimensions checkboxes
      otherCheckboxes.forEach(cb => cb.checked = false);
      // Set the hidden input's value and add the name attribute
      hiddenInput.value = value;
      hiddenInput.setAttribute('name', 'properties[Dimensions]');
    } else {
      // If unchecked, clear the hidden input's value and remove the name attribute
      hiddenInput.value = '';
      hiddenInput.removeAttribute('name');
    }

    // Update the add to cart button state
    this.updateAddToCartState();
  }

  updateColorOptions() {
    if (!this.colorSelect) return;

    const selectedThickness = this.thicknessSelect.value;
    const variantOptions = this.casesVariantSelect.querySelectorAll('option');
    const availableColors = new Set();

    // Find all colors available for the selected thickness
    variantOptions.forEach(option => {
      const thickness = option.dataset.thickness.replace('"', '');
      if (!selectedThickness || thickness === selectedThickness) {
        const color = option.dataset.color;
        if (color) {
          availableColors.add(color);
        }
      }
    });

    // Update color dropdown options
    const currentColor = this.colorSelect.value;
    const colorOptions = this.colorSelect.querySelectorAll('option');

    colorOptions.forEach(option => {
      if (option.value === '') {
        // Keep the default "Select Color" option
        return;
      }

      if (!selectedThickness) {
        // If no thickness selected, show all colors
        option.hidden = false;
        option.disabled = false;
      } else {
        // Show only colors available for selected thickness
        const shouldShow = availableColors.has(option.value);
        option.hidden = !shouldShow;
        option.disabled = !shouldShow;
      }
    });

    // Reset color selection if current color is not available
    if (currentColor && !availableColors.has(currentColor) && selectedThickness) {
      this.colorSelect.value = '';
    }
  }

  updateVariantSelection() {
    const selectedThickness = this.thicknessSelect.value;
    const selectedColor = this.colorSelect ? this.colorSelect.value : null;

    const variantOptions = this.casesVariantSelect.querySelectorAll('option');
    let variantFound = false;

    variantOptions.forEach(option => {
      const thickness = option.dataset.thickness.replace('"', '');
      const color = option.dataset.color;

      const thicknessMatches = thickness === selectedThickness;
      const colorMatches = !this.colorSelect || !selectedColor || color === selectedColor;

      if (thicknessMatches && colorMatches) {
        option.selected = true;
        this.currentVariant = option;
        this.currentPricing = {
          basePrice: parseFloat(option.dataset.basePrice) || 0,
          sfPrice: parseFloat(option.dataset.sfPrice) || 0,
          minPrice: parseFloat(option.dataset.minPrice) || 0
        };
        variantFound = true;
      }
    });

    if (!variantFound) {
      this.currentVariant = null;
      this.currentPricing = {};
    }

    this.updateCalculation();
    this.updateAddToCartState();
  }

  updateCalculation() {
    if (!this.currentVariant || !this.currentPricing.basePrice || !this.currentPricing.sfPrice) {
      this.clearDisplays();
      return;
    }

    const length = parseFloat(this.caseLengthInput.value) || 0;
    const width = parseFloat(this.caseWidthInput.value) || 0;
    const height = parseFloat(this.caseHeightInput.value) || 0;

    if (length > 0 && width > 0 && height > 0) {
      const lw = length * width;
      const lh = length * height;
      const wh = width * height;

      const surfaceArea = (lw) + (2 * lh) + (2 * wh);
      const squareFeet = surfaceArea / 144;

      const calculatedPrice = (squareFeet * this.currentPricing.basePrice) * this.currentPricing.sfPrice;
      const finalPrice = Math.max(calculatedPrice, this.currentPricing.minPrice);

      this.updateMainPrice(finalPrice);
    } else {
      this.clearDisplays();
    }

    this.updateAddToCartState();
  }

  updateMainPrice(price) {
    if (!this.priceElement) return;

    const priceItem = this.priceElement.querySelector('.amount');
    const simplePriceHTML = `<span class="price-item price-item--regular">$${price.toFixed(2)}</span>`;

    if (priceItem) {
      priceItem.innerHTML = `$${price.toFixed(2)}`;
    } else {
      this.priceElement.innerHTML = simplePriceHTML;
    }
  }

  clearDisplays() {
    if (this.priceElement) {
      const priceItem = this.priceElement.querySelector('.amount');
      if (priceItem) {
        priceItem.innerHTML = '$0.00';
      } else {
        this.priceElement.innerHTML = '<span class="price-item price-item--regular">$0.00</span>';
      }
    }
  }

  updateAddToCartState() {
    const thicknessSelected = this.thicknessSelect.value !== '';
    const colorSelected = !this.colorSelect || this.colorSelect.value !== '';
    const length = parseFloat(this.caseLengthInput.value) || 0;
    const width = parseFloat(this.caseWidthInput.value) || 0;
    const height = parseFloat(this.caseHeightInput.value) || 0;
    const validDimensions = length > 0 && width > 0 && height > 0;

    // Check if one of the dimension checkboxes is selected
    const dimensionsHiddenInput = this.querySelector('[data-dimensions-value]');
    const dimensionSelected = dimensionsHiddenInput && dimensionsHiddenInput.value !== '';

    const canAddToCart = thicknessSelected && colorSelected && validDimensions && dimensionSelected && this.currentVariant;

    // Check if any dimension exceeds 36 inches
    const exceedsShippingLimit = length > 36 || width > 36 || height > 36;
    this.shippingInfo.style.display = (canAddToCart && exceedsShippingLimit) ? 'block' : 'none';

    this.addToCartButton.disabled = !canAddToCart;
    this.addToCartButton.hidden = !canAddToCart;
    this.addToCartButton.style.display = canAddToCart ? 'inline-flex' : 'none';
  }

  onSubmitHandler(evt) {
    evt.preventDefault();

    if (this.addToCartButton.classList.contains('loading')) return;

    this.addToCartButton.setAttribute('aria-disabled', true);
    this.addToCartButton.classList.add('loading');
    this.handleErrorMessage();

    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      }
    };

    let formData = new FormData(this.form);

    // Calculate and add weight property
    const length = parseFloat(this.caseLengthInput.value) || 0;
    const width = parseFloat(this.caseWidthInput.value) || 0;
    const height = parseFloat(this.caseHeightInput.value) || 0;
    const thickness = this.parseFraction(this.thicknessSelect.value);
    const weight = ((length * width) + (length * height) * 2 + (width * height) * 2) * thickness * 0.043;
    formData.append('properties[Weight]', weight.toFixed(3));

    formData.append('sections', this.getSectionsToRender().map((section) => section.section));
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(`${theme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }
        this.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.addToCartButton.classList.remove('loading');
        this.addToCartButton.removeAttribute('aria-disabled');
      });
  }

  getSectionsToRender() {
    return [{
      id: 'Cart-Drawer',
      section: 'cart-drawer',
      selector: '.cart-drawer'
    },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section => {
      if (!document.getElementById(section.id)) {
        return;
      }
      const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
      elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
    }));

    this.body.classList.add('open-cc');
    document.getElementById('Cart-Drawer').classList.add('active');
    dispatchCustomEvent('cart-drawer:open');
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    if (!this.errorMessageWrapper) return;
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }
}

customElements.define('cases-trays-form', CasesTraysForm);